import { Button, Icon, Modal } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"

import { useAppSelector } from "@/App"
import { selectDriverById, updateDriver } from "@/store/drivers"
import { Driver } from "@/types"
import { fetchDeleteBreakById } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import cls from "../DriverContextMenu.module.scss"

type DriversBreakDeleteModalProps = {
  driverId: Driver["id"]
  handleClose: () => void
}

export const DriversBreakDeleteModal = ({ driverId, handleClose }: DriversBreakDeleteModalProps) => {
  const driver = useAppSelector(selectDriverById(driverId))

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  if (!driver) return null

  async function handleSubmit(breakId: number) {
    if (!driver || !driver.today_breaks) return null
    setIsSubmitting(true)

    try {
      const { response, json } = await fetchDeleteBreakById(driverId, breakId)
      const updatedDriver = json?.data

      if (response.ok && updatedDriver) {
        toast.success(t("drivers:delete_break_success"))
        dispatch(updateDriver(updatedDriver))
      } else {
        handleStandardError(response, json)
      }
    } catch (error) {
      toast.error(t("drivers:delete_break_fail"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      handleClose={handleClose}
      className={clsx(cls.driversBreakModal, cls.driversBreakModalSmall)}
      closeBtnPosition="inside"
    >
      <Modal.Title>
        {t("drivers:courier")}: {driver.name}
        <div className="h300 mt-4">{t("drivers:delete_break")}</div>
      </Modal.Title>

      <Modal.Content>
        {driver.today_breaks.map((todayBreak) => {
          const hasBreakEnded = dayjs().isAfter(dayjs(todayBreak.end_time, "HH:mm"))
          return (
            <div className="d-flex justify-between align-center mb-6" key={`todayBreak-${todayBreak.id}`}>
              <div className="h200">Start: {todayBreak.start_time}</div>
              <div className="h200">Koniec: {todayBreak.end_time}</div>
              <Button
                variation="outlined"
                color="alert"
                size="small"
                type="submit"
                onClick={() => handleSubmit(todayBreak.id)}
                disabled={hasBreakEnded}
              >
                <Icon name="close-tiny" />
              </Button>
            </div>
          )
        })}

        <div className={cls.actions}>
          <Button onClick={handleClose} color="muted" variation="outlined" disabled={isSubmitting}>
            {t("cancel")}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  )
}
